import { useEffect, useMemo, useState } from 'react'
import { Bell, Loader2, Megaphone, Send, User, StopCircle } from 'lucide-react'
import {
  announceSurvey,
  fireTestNotification,
  getSurveyStatus,
  getSurveyUrl,
  listAdminNotificationTypes,
  setSurveyUrl,
  stopSurvey,
  type AdminNotificationType,
} from '../../lib/api'
import { StyledSelect, type StyledSelectOption } from '../ui/StyledSelect'
import { useAnnouncement } from '../../hooks/useAnnouncement'

const PRIORITY_BADGE: Record<string, string> = {
  high: 'bg-red-500/20 text-red-500',
  medium: 'bg-amber-500/20 text-amber-500',
  low: 'bg-slate-500/20 text-slate-400',
}

const PRIORITY_LABEL: Record<string, string> = {
  high: 'High priority',
  medium: 'Medium priority',
  low: 'Low priority',
}

/**
 * Admin notification controls, embedded in the Platform Settings page:
 *  - Test notifications: fire any notification type at a target user.
 *  - Survey announcement: configure the survey link, preview to yourself, and
 *    broadcast the (gated) announcement to all users.
 *
 * Submitted NPS star results live in Admin → Analytics → Survey.
 */
export function NotificationsAdmin() {
  const { openDialog } = useAnnouncement()
  const [types, setTypes] = useState<AdminNotificationType[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [selectedType, setSelectedType] = useState<string>('')
  const [targetEmail, setTargetEmail] = useState('')
  const [appName, setAppName] = useState('')
  const [firing, setFiring] = useState(false)
  const [lastResult, setLastResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  // Survey announcement broadcast (admin-triggered, replaces the deploy-time fan-out)
  const [announcing, setAnnouncing] = useState<'self' | 'all' | null>(null)
  const [surveyResult, setSurveyResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  // Survey link override (empty = use the built-in default).
  const [surveyUrl, setSurveyUrlState] = useState('')
  const [savingUrl, setSavingUrl] = useState(false)
  const [urlResult, setUrlResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  // Live announcement status + take-down control.
  const [status, setStatus] = useState<{ active: boolean; epoch: number } | null>(null)
  const [stopping, setStopping] = useState(false)

  function refreshStatus() {
    getSurveyStatus()
      .then(setStatus)
      .catch(() => {
        // Non-critical — the status line just won't render.
      })
  }

  const typeOptions: StyledSelectOption[] = useMemo(
    () =>
      types.map((t) => ({
        value: t.type,
        label: t.type,
        description: PRIORITY_LABEL[t.priority] ?? t.priority,
      })),
    [types],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listAdminNotificationTypes()
      .then((res) => {
        if (cancelled) return
        setTypes(res.types)
        if (res.types.length > 0) setSelectedType(res.types[0].type)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    getSurveyUrl()
      .then((res) => {
        if (!cancelled) setSurveyUrlState(res.survey_url)
      })
      .catch(() => {
        // Non-critical — leave the field empty (defaults still apply).
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    refreshStatus()
  }, [])

  async function onStopSurvey() {
    if (stopping) return
    if (
      !window.confirm(
        'Take the announcement down? This stops the modal for everyone and clears the ' +
          'unread bell notifications. (You can broadcast again later.)',
      )
    ) {
      return
    }
    setStopping(true)
    setSurveyResult(null)
    try {
      const res = await stopSurvey()
      setSurveyResult({
        kind: 'ok',
        text: `Announcement stopped — cleared ${res.cleared} bell notification${res.cleared === 1 ? '' : 's'}.`,
      })
      refreshStatus()
    } catch (err) {
      setSurveyResult({ kind: 'err', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setStopping(false)
    }
  }

  async function onSaveSurveyUrl() {
    setSavingUrl(true)
    setUrlResult(null)
    try {
      const res = await setSurveyUrl(surveyUrl.trim())
      setSurveyUrlState(res.survey_url)
      setUrlResult({
        kind: 'ok',
        text: res.survey_url
          ? 'Survey link saved — the modal will open this URL.'
          : 'Survey link cleared — the modal will use the built-in default.',
      })
    } catch (err) {
      setUrlResult({ kind: 'err', text: err instanceof Error ? err.message : String(err) })
    } finally {
      setSavingUrl(false)
    }
  }

  async function onFire() {
    if (!selectedType || !targetEmail) return
    setFiring(true)
    setLastResult(null)
    try {
      const res = await fireTestNotification({
        notification_type: selectedType,
        target_user_email: targetEmail.trim(),
        app_name: appName.trim() || undefined,
      })
      setLastResult({
        kind: 'ok',
        text: `Fired ${res.notification_type} → ${res.target_user_email}`,
      })
    } catch (err) {
      setLastResult({
        kind: 'err',
        text: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setFiring(false)
    }
  }

  async function onAnnounceSurvey(scope: 'self' | 'all') {
    if (announcing) return
    if (
      scope === 'all' &&
      !window.confirm(
        'Broadcast the survey invite to EVERY active user ' +
          '(in-app bell + auto-show modal) and make it the live announcement? ' +
          'This is the real announcement — not a test.',
      )
    ) {
      return
    }
    setAnnouncing(scope)
    setSurveyResult(null)
    try {
      const res = await announceSurvey(scope)
      if (res.deduped) {
        setSurveyResult({
          kind: 'err',
          text: 'Skipped — an identical broadcast just went out moments ago. Wait ~30s before re-sending.',
        })
      } else {
        if (scope === 'self') {
          // Preview the modal right here — open it immediately rather than
          // relying on the throttled on-navigation re-check, and unconditionally
          // so it shows even if this admin already took the survey.
          openDialog()
          setSurveyResult({
            kind: 'ok',
            text: 'Sent the survey invite to yourself — previewing what users will see (also in your bell).',
          })
        } else {
          setSurveyResult({
            kind: 'ok',
            text: `Broadcast the survey invite to ${res.recipients} user${res.recipients === 1 ? '' : 's'} (now live).`,
          })
          refreshStatus()
        }
      }
    } catch (err) {
      setSurveyResult({
        kind: 'err',
        text: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setAnnouncing(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Test notifications card */}
      <div className="border border-border rounded-xl bg-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-2">
          <Bell className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-semibold">Test notifications</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Fire any notification type at a target user with stub data. Useful for verifying
          in-app bell rendering, push delivery, and deep-link routing without waiting for
          a real platform event. The recipient&apos;s notification preferences are respected
          — disable a type in their Settings to verify suppression.
        </p>

        {loadError ? (
          <div className="p-4 rounded-lg bg-red-500/10 text-red-500 text-sm">
            Failed to load notification types: {loadError}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading types…
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Notification type</label>
              <StyledSelect
                value={selectedType}
                onChange={setSelectedType}
                className="w-full justify-between"
                options={typeOptions}
              />
              {selectedType ? (
                <div className="mt-2">
                  <span
                    className={`inline-block px-2 py-0.5 text-xs rounded ${
                      PRIORITY_BADGE[types.find((t) => t.type === selectedType)?.priority ?? 'low']
                    }`}
                  >
                    priority: {types.find((t) => t.type === selectedType)?.priority}
                  </span>
                </div>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Target user email</label>
              <input
                type="email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                placeholder="user@exante.eu"
                className="w-full p-2 rounded-lg border border-border bg-background"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                App name <span className="text-muted-foreground">(optional — defaults to &quot;test-app&quot;)</span>
              </label>
              <input
                type="text"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="my-app"
                className="w-full p-2 rounded-lg border border-border bg-background"
              />
            </div>

            <button
              onClick={onFire}
              disabled={firing || !selectedType || !targetEmail}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
            >
              {firing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {firing ? 'Firing…' : 'Fire notification'}
            </button>

            {lastResult ? (
              <div
                className={`p-3 rounded-lg text-sm ${
                  lastResult.kind === 'ok' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                }`}
              >
                {lastResult.text}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Survey announcement card */}
      <div className="border border-border rounded-xl bg-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-semibold">Survey announcement</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Broadcast the platform survey invite (in-app bell + auto-show modal, no push). This is the
          real announcement — it goes out when you trigger it here, not on deploy. Recipients&apos;
          notification preferences are respected, and re-firing updates the existing unread invite
          rather than stacking duplicates. Send it to yourself first to preview exactly what users
          will see. Star results appear under Analytics → Survey.
        </p>

        {/* Live status + take-down. Visible whenever an announcement is active. */}
        {status?.active ? (
          <div className="mb-4 flex flex-col gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm">
              <span className="font-medium text-amber-600 dark:text-amber-400">Live now</span> — round #{status.epoch}.
              Users see it on their next dashboard visit.
            </span>
            <button
              onClick={onStopSurvey}
              disabled={stopping}
              className="flex shrink-0 items-center justify-center gap-2 rounded-lg border border-red-500/40 bg-background px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-50"
            >
              {stopping ? <Loader2 className="w-4 h-4 animate-spin" /> : <StopCircle className="w-4 h-4" />}
              {stopping ? 'Stopping…' : 'Stop announcement'}
            </button>
          </div>
        ) : null}

        {/* Survey link — the URL the modal's CTA opens. Empty = built-in default. */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Survey link <span className="text-muted-foreground">(leave empty to use the default)</span>
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="url"
              value={surveyUrl}
              onChange={(e) => setSurveyUrlState(e.target.value)}
              placeholder="https://cc.exante.eu/a/cc-survey/s/…"
              className="flex-1 p-2 rounded-lg border border-border bg-background"
            />
            <button
              onClick={onSaveSurveyUrl}
              disabled={savingUrl}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-50"
            >
              {savingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {savingUrl ? 'Saving…' : 'Save link'}
            </button>
          </div>
          {urlResult ? (
            <div
              className={`mt-2 p-2 rounded-lg text-sm ${
                urlResult.kind === 'ok' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
              }`}
            >
              {urlResult.text}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => onAnnounceSurvey('self')}
            disabled={announcing !== null}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-50"
          >
            {announcing === 'self' ? <Loader2 className="w-4 h-4 animate-spin" /> : <User className="w-4 h-4" />}
            {announcing === 'self' ? 'Sending…' : 'Send to myself'}
          </button>

          <button
            onClick={() => onAnnounceSurvey('all')}
            disabled={announcing !== null}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {announcing === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            {announcing === 'all' ? 'Broadcasting…' : 'Broadcast to all users'}
          </button>
        </div>

        {surveyResult ? (
          <div
            className={`mt-4 p-3 rounded-lg text-sm ${
              surveyResult.kind === 'ok' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
            }`}
          >
            {surveyResult.text}
          </div>
        ) : null}
      </div>
    </div>
  )
}
